/**
 * SWR-based API hooks for pledge operations
 * Provides data fetching and caching for pledges with appropriate revalidation settings
 */
import useSWR from "swr";
import { api, useServerState, createHeaders } from "@project-mgmt/api-client";
/**
 * SWR configuration for pledge data
 * More aggressive revalidation for pledge-related data
 */
const pledgeSWRConfig = {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 5000,
    shouldRetryOnError: true,
    errorRetryCount: 3,
};
/**
 * Hook to fetch pledges for a specific venture
 * Requirement 13.1, 13.2: Fetch venture pledges
 *
 * @param workspaceId - The workspace ID for context
 * @param ventureId - The venture ID to fetch pledges for
 * @returns SWR response with pledge list data
 */
export function useVenturePledges(workspaceId, ventureId) {
    // Return fallback response if required parameters are missing
    if (!workspaceId || !ventureId) {
        return {
            pledges: [],
            isLoading: false,
            error: "Missing workspace ID or venture ID",
            refetch: async () => {
                throw new Error("Cannot refetch: missing workspace ID or venture ID");
            },
        };
    }
    const { data, error, isLoading, refetch } = useServerState({
        key: ["ventures", ventureId, "pledges", workspaceId],
        fetcher: async (_, signal) => {
            const headers = createHeaders(workspaceId);
            const response = await api.get(`/api/ventures/${ventureId}/pledges`, {
                headers,
                signal,
            });
            return response.data;
        },
        enabled: Boolean(workspaceId && ventureId),
        config: pledgeSWRConfig,
    });
    return {
        pledges: data?.pledges || [],
        isLoading,
        error: error?.message || null,
        refetch,
    };
}
/**
 * Hook to fetch pledges made by the current user
 * Requirement 13.1, 13.2, 13.3, 13.4, 13.5: User's pledge history
 *
 * @returns SWR response with user's pledge list including venture details
 */
export function useMyPledges() {
    const { data, error, isLoading, mutate } = useSWR("/api/users/me/pledges", async (url) => {
        const response = await api.get(url);
        return response.data;
    }, pledgeSWRConfig);
    return {
        pledges: data || [],
        isLoading,
        error: error?.message || null,
        mutate,
    };
}
